import React, { useEffect, useState } from "react";
import { Logo, Visibility, VisibilityOff } from "../assets/icons/Icons";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../lib/contexts/UserContext";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { QRCodeSVG } from "qrcode.react";
import { setCookie } from "../lib/utils/cookies";
import { getUserDetails, userQRLogin } from "../lib/apis/user";
import { v4 as uuidv4 } from "uuid";
import { encryptSessionId } from "../lib/apis/qrLogin";
import { toast } from "react-toastify";
import { baseURLForQrLogin } from "../lib/utils/constants";

const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [loginType, setLoginType] = useState("QR");
  const [sessionId, setSessionId] = useState("");
  const [encryptedSessionId, setEncryptedSessionId] = useState("");
  const [isIdGenerating, setIsIdGenerating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds

  const { handleLogin, userData, setAccessToken, setUserData } = useUserData();
  const { allHospData, setHospitalID } = useHospDocData();
  const navigate = useNavigate();

  // Function to check session status
  const checkSessionStatus = async () => {
    try {
      const response = await userQRLogin(sessionId);
      if (response?.status === 200 || response?.status === 201) {
        // Wait for animation to complete before stopping polling
        setIsAuthenticated(true);
        setTimeout(async () => {
          // Handle successful authentication with direct accessToken and userId
          const { access_token, user_id } = response.data.result;
          setAccessToken(access_token);
          setCookie("userID", user_id, 7);

          // Fetch user details
          const user_data = await getUserDetails(user_id);
          if (user_data?.status === 200) {
            setUserData(user_data.data.result);
            setIsPolling(false);
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking session:", error);
    }
  };

  const generateNewEncryptedSessionId = async () => {
    setIsIdGenerating(true);
    const newSessionId = uuidv4();
    const result = await encryptSessionId(newSessionId);
    if (result?.status === 200) {
      setSessionId(newSessionId);
      setEncryptedSessionId(result.data.encryptedSessionId);
    } else {
      toast.error("Failed to generate session ID. Please try again.");
    }
    setIsIdGenerating(false);
  };

  useEffect(() => {
    generateNewEncryptedSessionId();
  }, [loginType]);

  // Polling effect
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (loginType === "QR" && sessionId) {
      setIsPolling(true);
      pollInterval = setInterval(checkSessionStatus, 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setIsPolling(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginType, sessionId]);

  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (loginType === "QR" && !isAuthenticated && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            generateNewEncryptedSessionId();
            return 600; // Reset to 10 minutes
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [loginType, isAuthenticated, timeLeft]);

  // Reset timer when generating new session ID
  useEffect(() => {
    setTimeLeft(600);
  }, [encryptedSessionId]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    if (allHospData && allHospData.length > 0) {
      if (allHospData.length > 1) {
        navigate("/admin");
      } else {
        const hospitalId = allHospData[0]?.hospital_id;
        if (hospitalId) {
          setHospitalID(hospitalId);
          navigate(`/${hospitalId}/profiles`);
        }
      }
    }
  }, [userData, allHospData, navigate, setHospitalID]);

  return (
    <div className="flex flex-row w-full h-screen">
      <div className="w-1/2 max-h-screen hidden md:flex">
        <img
          src={require("../assets/images/signin.png")}
          alt="Sign in"
          className="object-cover w-full max-h-screen"
        />
      </div>
      <div className="rounded-2xl border-[1px] border-white bg-white md:pt-10 px-10 lg:px-[156px] flex flex-col justify-center items-center w-full md:w-1/2 gap-10 overflow-y-auto">
        <div className={`${isAuthenticated ? "hidden" : "block"}`}>
          <Logo />
        </div>
        {loginType === "QR" ? (
          <div className="flex flex-col items-center w-full">
            {isAuthenticated && (
              <div className="flex flex-col items-center scale-[1.5]">
                <div className="success-checkmark">
                  <div className="check-icon">
                    <span className="icon-line line-tip"></span>
                    <span className="icon-line line-long"></span>
                    <div className="icon-circle"></div>
                    <div className="icon-fix"></div>
                  </div>
                </div>
                <p className="text-green-500 text-sm mt-6 text-[#4CAF50]">
                  Authentication Successful!
                </p>
              </div>
            )}
            {!isAuthenticated && (
              <>
                <p className="text-dark text-xl md:text-2xl font-bold text-center mb-2">
                  Scan QR Code to Login
                </p>
                <div className="text-sm text-gray-600 mb-1">
                  Session Expires in:{" "}
                  <span className="font-semibold text-red-400">
                    {formatTime(timeLeft)}
                  </span>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-lg mb-4">
                  <QRCodeSVG
                    value={`${baseURLForQrLogin}/qrsignin/${encryptedSessionId}`}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <button
                  onClick={generateNewEncryptedSessionId}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white font-medium shadow transition-all duration-150 text-sm my-4"
                  type="button"
                  disabled={isIdGenerating}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    x="0px"
                    y="0px"
                    width="24"
                    height="24"
                    viewBox="0 0 256 256"
                    className="w-4 h-4 relative top-[1px]"
                  >
                    <g
                      fill="#ffffff"
                      fillRule="nonzero"
                      stroke="none"
                      strokeWidth="1"
                      strokeLinecap="butt"
                      strokeLinejoin="miter"
                      strokeMiterlimit="10"
                      strokeDasharray=""
                      strokeDashoffset="0"
                      fontFamily="none"
                      fontWeight="none"
                      fontSize="none"
                      textAnchor="none"
                      style={{ mixBlendMode: "normal" }}
                    >
                      <g transform="scale(8.53333,8.53333)">
                        <path d="M15,3c-2.9686,0 -5.69718,1.08344 -7.79297,2.875c-0.28605,0.22772 -0.42503,0.59339 -0.36245,0.95363c0.06258,0.36023 0.31676,0.6576 0.66286,0.77549c0.3461,0.1179 0.72895,0.03753 0.99842,-0.20959c1.74821,-1.49444 4.01074,-2.39453 6.49414,-2.39453c5.19656,0 9.45099,3.93793 9.95117,9h-2.95117l4,6l4,-6h-3.05078c-0.51129,-6.14834 -5.67138,-11 -11.94922,-11zM4,10l-4,6h3.05078c0.51129,6.14834 5.67138,11 11.94922,11c2.9686,0 5.69718,-1.08344 7.79297,-2.875c0.28605,-0.22772 0.42504,-0.59339 0.36245,-0.95363c-0.06258,-0.36023 -0.31676,-0.6576 -0.66286,-0.7755c-0.3461,-0.1179 -0.72895,-0.03753 -0.99842,0.20959c-1.74821,1.49444 -4.01074,2.39453 -6.49414,2.39453c-5.19656,0 -9.45099,-3.93793 -9.95117,-9h2.95117z" />
                      </g>
                    </g>
                  </svg>
                  <span>{isIdGenerating ? "Generating.." : "Regenerate"}</span>
                </button>
                <p className="text-xs md:text-sm mb-4">
                  Login using form?{" "}
                  <span
                    className="text-blue-400 cursor-pointer"
                    onClick={() => setLoginType("Form")}
                  >
                    Click here
                  </span>
                </p>
                {isPolling && !isAuthenticated && (
                  <p className="text-sm text-gray-500 animate-pulse">
                    Waiting for authentication...
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <form
            className="mt-28 flex flex-col w-full"
            onSubmit={(e) => handleLogin({ email, password }, e)}
          >
            <p className="text-dark text-xl md:text-2xl font-bold text-left mb-11">
              Sign In for DocTrue
            </p>
            <p className="text-dark text-xs md:text-sm mb-2">Email Address</p>
            <input
              className="rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full mb-4 focus:outline-none"
              placeholder="Enter Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              minLength={0}
              id="email"
              required
            />
            <p className="text-dark text-xs md:text-sm mb-2">Password</p>
            <div className="flex justify-between items-center rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full mb-2 bg-white">
              <input
                className="border-0 p-0 focus:ring-0 w-full mr-5 focus:outline-none"
                placeholder="8+ characters"
                type={visible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                id="password"
                required
              />
              <span
                onClick={() => setVisible(!visible)}
                className="cursor-pointer"
              >
                {visible ? <VisibilityOff /> : <Visibility />}
              </span>
            </div>
            <p className="text-xs md:text-sm mb-9">
              Login using QR?
              <span
                className="text-blue-400 cursor-pointer"
                onClick={() => setLoginType("QR")}
              >
                {" "}
                Click here
              </span>
            </p>

            <button
              type="submit"
              disabled={email.length === 0 && password.length === 0}
              className={`rounded-lg w-full text-sm font-semibold ${
                email.length !== 0 && password.length !== 0
                  ? "bg-sbTextHover text-white"
                  : "bg-signinButton text-signinText"
              }  p-4 mb-36`}
              // onClick={(e) => handleLogin({ email, password }, e)}
            >
              Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Signin;
